import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import Table from '@gp/components/GeneralTable';
import Filter from './components/Filter';
import { FILE_TYPES2, FILE_TYPES_MAP } from '@gp/constants/const';
import { FilterItemType } from '@gp/components/GeneralSelectFilter';
import { IFilter } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/People/Details/components/Filter';
import { SortBy } from 'tea-component/lib/table/addons';
import classNames from 'classnames';
import { TableListOptionsType } from '@gp/components/GeneralTable/index.d';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import './index.scss';

interface GrossRevenueCardPropsType {
  tableClassName: string;
  title: JSX.Element | null;
  filterLoading: boolean;
  filterTimeRangeData: {
    startDate: string;
    endDate: string;
  } | null;
  filterOptions: FilterItemType[];
  filterChange:
    | ((studio: string[], project: string[], date: IFilter, ym: string[], handleFilterName: string) => void)
    | null;
  tableLoading: boolean;
  tableOptions: TableListOptionsType | null;
  tableRecords: any; // 数组每一项要有uniqueId
  onTableSort: ((v: SortBy<any>) => void) | null;
  downloadParams: {
    select_id: string[];
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  } | null;
  downloadServices: any;
  downloadLogKey: {
    csv: string;
    excel: string;
  } | null;
}

const DEFAULT_PROPS: GrossRevenueCardPropsType = {
  tableClassName: '',
  title: null,
  filterLoading: true,
  filterTimeRangeData: null,
  filterOptions: [],
  filterChange: null,
  tableLoading: true,
  tableOptions: null,
  tableRecords: null,
  onTableSort: null,
  downloadParams: null,
  downloadServices: null,
  downloadLogKey: null,
};

const GrossRevenueCard: React.FC<GrossRevenueCardPropsType> = (props) => {
  const {
    tableClassName,
    title,
    filterLoading,
    filterTimeRangeData,
    filterOptions,
    filterChange,
    tableLoading,
    tableOptions,
    tableRecords,
    onTableSort,
    downloadParams,
    downloadServices,
    downloadLogKey,
  } = props;

  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey: downloadLogKey?.csv || '',
    excelDownloadLogKey: downloadLogKey?.excel || '',
    downloadService: createDownloadService(downloadServices),
  });

  const [tableVirtualLoading, setTableVirtualLoading] = useState<boolean>(false); // 表格内部虚拟loading
  const [pageIndex, setPageIndex] = useState<number>(1);
  const handleVirtualLoading = useCallback((v) => {
    setTableVirtualLoading(v || false);
  }, []);
  const handlePagingChange = useCallback((v) => {
    setPageIndex(v?.pageIndex || 1);
  }, []);

  const tabs = useMemo(() => {
    return [
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES2}
            isLoading={isDownloading}
            onConfirm={(payload) => {
              const fileType = payload?.fileType || FILE_TYPES_MAP.CSV;
              handleDownload({
                ...downloadParams,
                fileType,
                file_type: Number.parseInt(fileType, 10),
              });
            }}
          />
        ),
        component: (
          <section
            className={classNames({
              'dbg-studio-monthly-report-gross-revenue-card-body': true,
              'dbg-studio-list-first-line': !tableLoading && !tableVirtualLoading && tableRecords && tableRecords?.length && pageIndex < 2,
            })}
          >
            <Filter
              loading={filterLoading}
              timeRangeData={filterTimeRangeData}
              options={filterOptions}
              onChange={filterChange}
            />
            <Table
              className={tableClassName}
              loading={tableLoading}
              tableOptions={tableOptions}
              tableRecords={tableRecords}
              isTableBodyMaxH={false}
              onSort={onTableSort}
              onPagingChange={handlePagingChange}
              onVirtualLoading={handleVirtualLoading}
            />
          </section>
        ),
      },
    ];
  }, [tableClassName, tableLoading, filterLoading, tableVirtualLoading, pageIndex, isDownloading, downloadParams, handleDownload]);

  return (
    <TriggerViewCard
      className="dbg-studio-monthly-report-gross-revenue-card"
      title={title}
      loading={false}
      isNoData={false}
      tabs={tabs}
      defaultActiveId="download"
    />
  );
};

GrossRevenueCard.defaultProps = DEFAULT_PROPS;

export default memo(GrossRevenueCard);
